// Search + section metadata for the Settings page, kept in a pure module so the
// taxonomy and the keyword-matching are unit-testable without rendering the page.

export interface SettingsCardMeta {
  id: string;
  section: string;
  title: string;
  /** Danish synonyms so "moms"/"logo"/"løn" find the right card. */
  keywords: string;
}

// Section taxonomy. Order = render order = chip order.
export const SECTIONS: { id: string; label: string }[] = [
  { id: 'virksomhed', label: 'Virksomhed' },
  { id: 'numre', label: 'Numre & fakturering' },
  { id: 'projekter', label: 'Projekter & tilbud' },
  { id: 'roller', label: 'Medarbejdere & roller' },
  { id: 'integrationer', label: 'Integrationer' },
  { id: 'avanceret', label: 'Avanceret' },
];

export const CARD_META: SettingsCardMeta[] = [
  { id: 'company', section: 'virksomhed', title: 'Virksomhedsoplysninger', keywords: 'logo cvr moms bank iban swift konto adresse hjemmeside telefon email morarente' },
  { id: 'dpa', section: 'virksomhed', title: 'Databehandleraftale', keywords: 'dpa gdpr databehandler aftale underdatabehandler persondata accept pdf' },
  { id: 'case', section: 'numre', title: 'Sagsnummer', keywords: 'sag projekt præfiks løbenummer nummerering' },
  { id: 'invoice', section: 'numre', title: 'Fakturanummer', keywords: 'faktura præfiks nummerering' },
  { id: 'quote-number', section: 'numre', title: 'Tilbudsnummer', keywords: 'tilbud udbud præfiks løbenummer nummerering fortløbende' },
  { id: 'workflow', section: 'projekter', title: 'Projekt-workflow', keywords: 'status kvalitetskontrol kls automatisk påmindelse startdato slutdato' },
  { id: 'kls', section: 'projekter', title: 'KLS', keywords: 'kls kvalitetsledelse kvalitetskontrol fagområde branche elektriker vvs ventilation tømrer generel ai' },
  { id: 'project-statuses', section: 'projekter', title: 'Projektstatusser', keywords: 'status statusser projektstatus stadie fase farve standard skjul slet omdøb' },
  { id: 'quote-defaults', section: 'projekter', title: 'Tilbudsindstillinger', keywords: 'tilbud betalingsbetingelser vilkår skabelon' },
  { id: 'material-markup', section: 'projekter', title: 'Pristrin-avance', keywords: 'avance materiale pris pristrin' },
  { id: 'economy', section: 'projekter', title: 'Økonomi', keywords: 'sagsbudget budget omkostningstyper kostpris timekostpris løn db dækningsbidrag dg stade mål fordeling danløn' },
  { id: 'service-orders', section: 'projekter', title: 'Serviceaftaler', keywords: 'serviceaftale serviceordre service forfald automatisk oprettelse kunde besked e-mail besøg' },
  { id: 'customer-score', section: 'projekter', title: 'Kunde-score', keywords: 'kunde score hitrate tilbud kalkulation faktor friktion strategisk' },
  { id: 'product-groups', section: 'projekter', title: 'Materialegrupper', keywords: 'faggrupper materialetyper avance gruppe materiale' },
  { id: 'calc-templates', section: 'projekter', title: 'Kalkulationsskabeloner', keywords: 'erhvervskalkulation beregning skabelon kategori' },
  { id: 'besig-templates', section: 'projekter', title: 'Besigtigelse-skabeloner', keywords: 'besigtigelse gennemgang skabelon kategori' },
  { id: 'time-registration', section: 'roller', title: 'Tidsregistrering', keywords: 'tidsregistrering timer stopur start stop manuel fra til tidspunkt medarbejder registrering' },
  { id: 'admin-time', section: 'roller', title: 'Opret tid for medarbejder', keywords: 'tid timer registrering medarbejder opret backfill uge dag admin glemt' },
  { id: 'timetypes', section: 'roller', title: 'Timetyper', keywords: 'timetype timetyper arbejdstid montørtime transport overarbejde kostpris løn' },
  { id: 'teams', section: 'roller', title: 'Teams', keywords: 'team teams afdeling gruppe grupper farve rækkefølge arkivér opret omdøb kontor mark montører' },
  { id: 'supplier-access', section: 'roller', title: 'Leverandøradgang', keywords: 'leverandør sælger byggemarked grossist adgang invitér inviter account manager rettigheder bilag' },
  { id: 'permissions', section: 'roller', title: 'Tilladelser', keywords: 'roller adgang bruger rettigheder' },
  { id: 'bonus', section: 'roller', title: 'Bonusordning', keywords: 'bonus løn timer avance' },
  { id: 'integrations', section: 'integrationer', title: 'Integrationer', keywords: 'e-conomic economic danløn dinero mesterchat kommunikation regnskab løn marketplace forbundet tilføj' },
  { id: 'economic', section: 'integrationer', title: 'e-conomic bogføring', keywords: 'economic bogføring konti debitor salg regnskab' },
  { id: 'communication', section: 'integrationer', title: 'Mesterchat', keywords: 'mesterchat chat kommunikation projektkommunikation beskeder sms email kundeportal notifikationer' },
  { id: 'danlon', section: 'integrationer', title: 'Danløn løneksport', keywords: 'danløn løn medarbejder lønkode eksport synkroniser timer' },
  { id: 'tegningsanalyse-credits', section: 'integrationer', title: 'AI-analyse — credits', keywords: 'ai-analyse ai analyse tegningsanalyse udbudsanalyse credits kredit anthropic api nøgle byo betaling stripe køb blueprint saldo' },
  { id: 'ai-assistants', section: 'integrationer', title: 'Claude & ChatGPT', keywords: 'claude chatgpt openai anthropic ai assistent mcp connector plugin forbind claude code' },
  { id: 'test-mode', section: 'avanceret', title: 'Test mode', keywords: 'test testtilstand email omdiriger sandbox' },
];

/** O(1) lookup by card id (ids are unique — asserted in the catalog test). */
export const CARD_BY_ID: ReadonlyMap<string, SettingsCardMeta> = new Map(
  CARD_META.map((c) => [c.id, c]),
);

/** Section label by id, so searching a category name (e.g. "Projekter") finds its cards. */
const SECTION_LABEL_BY_ID: ReadonlyMap<string, string> = new Map(SECTIONS.map((s) => [s.id, s.label]));

/** Permission + module flags that decide which settings cards/sections render. */
export interface SettingsGateFlags {
  canEditCompany: boolean;
  canManagePermissions: boolean;
  canManageBonus: boolean;
  canEditCalc: boolean;
  /** `quality.view`: opens the KLS card and (with it) the AI-analyse credits card. */
  canViewQuality: boolean;
  /** `timeEntries.editOthers`: opens "Opret tid for medarbejder". */
  canEditOthersTime: boolean;
  modCalc: boolean;
  modBesig: boolean;
  modComm: boolean;
  econConnected: boolean;
  danlonConnected: boolean;
  /** A DPA text is published, so the Databehandleraftale tile exists (it ships dark). */
  dpaAvailable?: boolean;
  /** Leverandøradgang card: admins on a backend that has it, and non-admins (who see the not-admin state). */
  supplierAccessVisible?: boolean;
}

/** Which cards are permitted to render for this admin (before any search filter). */
export function buildShowMap(f: SettingsGateFlags): Record<string, boolean> {
  return {
    company: true,
    dpa: f.dpaAvailable ?? false,
    case: true,
    invoice: true,
    'quote-number': true,
    workflow: f.canEditCompany,
    // Visible with quality.view alone; editing the default trade needs editCompany
    // (the card itself shows the read-only state).
    kls: f.canViewQuality,
    'project-statuses': f.canEditCompany,
    'quote-defaults': f.canEditCompany,
    'material-markup': f.canEditCompany,
    economy: f.canEditCompany,
    'service-orders': f.canEditCompany,
    'customer-score': f.canEditCompany,
    'product-groups': f.canEditCompany,
    'calc-templates': f.canEditCalc && f.modCalc,
    'besig-templates': f.canEditCompany && f.modBesig,
    // Was missing entirely, so `showMap['time-registration'] ?? false` hid the
    // Tidsregistrering card for everyone while it still matched the search.
    // Gated like its neighbours in the same section (timetypes, teams).
    'time-registration': f.canEditCompany,
    'admin-time': f.canEditOthersTime,
    timetypes: f.canEditCompany,
    teams: f.canEditCompany,
    'supplier-access': f.supplierAccessVisible ?? false,
    permissions: f.canManagePermissions,
    bonus: f.canManageBonus,
    integrations: true,
    economic: f.econConnected,
    // Mesterchat is a Marketplace module, so its settings card sits in the
    // Integrationer section alongside e-conomic/Danløn rather than under Projekter.
    communication: f.canEditCompany && f.modComm,
    danlon: f.danlonConnected,
    // The Tegningsanalyse credits/BYO-key block exists when the calculation module
    // is on OR the user can see KLS (KLS AI uses the same key and credits, so a
    // company without the calculation module must still reach it). Gating here — not
    // just relying on the component's own null-return — is what keeps the card out
    // of the search results and the section/chip; this is the single owner of the gate.
    'tegningsanalyse-credits': f.modCalc || f.canViewQuality,
    // Guidance only: every member may connect (reads), writes are role-checked by /mcp itself.
    'ai-assistants': true,
    'test-mode': f.canEditCompany,
  };
}

/**
 * Sections that exist for this admin (permission/module gating), in catalog
 * order. A section exists iff at least one of its cards is permitted — this is
 * asserted against buildShowMap in the catalog test so the two can't drift.
 */
export function existingSectionIds(f: SettingsGateFlags): string[] {
  const exists: Record<string, boolean> = {
    virksomhed: true,
    numre: true,
    projekter: f.canEditCompany || (f.canEditCalc && f.modCalc) || f.canViewQuality,
    roller: f.canManagePermissions || f.canManageBonus || f.canEditCompany || f.canEditOthersTime,
    integrationer: true,
    avanceret: f.canEditCompany,
  };
  return SECTIONS.filter((s) => exists[s.id]).map((s) => s.id);
}

/**
 * Does a card match the (already lower-cased, trimmed) search query? An empty
 * query matches everything. Matching is a substring test over the card's title,
 * its keyword synonyms, and its section label — so searching a category name
 * ("Projekter", "Integrationer") surfaces that section's cards.
 */
export function cardMatchesQuery(meta: SettingsCardMeta, normalizedQuery: string): boolean {
  if (!normalizedQuery) {
    return true;
  }
  const sectionLabel = SECTION_LABEL_BY_ID.get(meta.section) ?? '';
  return `${meta.title} ${meta.keywords} ${sectionLabel}`.toLowerCase().includes(normalizedQuery);
}
